import React, { useState } from 'react';

import { DismissButton } from '~/components/AiChat/SideBarDialog/Actions/Action/DismissButton';
import { DismissWithReasonDialog } from '~/components/Clive/CliveDialog/DismissWithReasonDialog';

import { ACTION_NEEDS_ATTENTION } from '../constants';
import type { AskState, ConductorRequest } from '../types';

import { useRejectRequest } from './useRejectRequest';

interface RequestGesturesProps {
  request: ConductorRequest;
  state: AskState;
  /** Refetches the asks after a gesture, which both panes' request cards resolve from. */
  onAskUpdate: () => Promise<void>;
}

/**
 * Reject on a request that waits on the handler, in either pane. Nothing once it is closed — Clive
 * closes a request when the work arrives, so Done has no button.
 *
 * No lock across the panes, as an action's gestures have: the dialog is modal and stays open until
 * the asks are read again, by when the request has settled in both.
 */
const RequestGestures: React.FC<RequestGesturesProps> = ({ request, state, onAskUpdate }) => {
  const reject = useRejectRequest(request.id, onAskUpdate);
  const [isRejectOpen, setIsRejectOpen] = useState(false);

  // A failure rethrows into the dialog, which stays open to retry with the note still written.
  const handleReject = async (reason: string): Promise<void> => {
    await reject(reason);
    setIsRejectOpen(false);
  };

  return (
    <>
      {ACTION_NEEDS_ATTENTION[state] ? (
        <div className="flex">
          <DismissButton label="Reject" onDismiss={() => setIsRejectOpen(true)} />
        </div>
      ) : null}
      {/* Mounted on its own flag, not on the state: the read that follows a reject settles the card
          before the dialog has closed. */}
      {isRejectOpen ? (
        <DismissWithReasonDialog
          title={request.ask}
          subject="request"
          confirmLabel="Reject"
          onConfirm={handleReject}
          onClose={() => setIsRejectOpen(false)}
        />
      ) : null}
    </>
  );
};

export default RequestGestures;
